import React from "react";
import { useCurrency } from "@/context/CurrencyContext";
import { useDateRange } from "@/context/DateRangeContext";
import { CURRENCY_SYMBOL } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Upload, ChevronDown, CalendarRange } from "lucide-react";
import ProductSearch from "@/components/ProductSearch";

const TITLES = {
  overview: "Sales Overview",
  cost: "Cost & Profit",
  history: "Product History",
  shipments: "FBA Shipments",
  warehouse: "Inventory",
  po: "Purchase Orders",
  othercosts: "Other Costs",
  "tx-us": "Transactions · Amazon US",
  "tx-eu": "Transactions · Amazon EU",
  orders: "Order Details",
  register: "Upload Register",
  guide: "Amazon Reports Guide",
  billing: "Plans & Billing",
  admin: "Admin · Plans & Features",
  pricing: "Admin · Pricing",
  cms: "Website Content (CMS)",
};

export default function TopHeader({ active, onUpload }) {
  const { currency, setCurrency, currencies, fxUpdatedAt } = useCurrency();
  const { preset, setPreset, presets } = useDateRange();
  const showFilters = active !== "guide";
  const activePreset = presets.find((p) => p.id === preset);
  return (
    <header className="sticky top-0 z-30 h-16 flex items-center justify-between px-4 md:px-8 border-b border-white/10 bg-black/60 backdrop-blur-xl">
      <div>
        <h1 className="font-head text-lg md:text-xl font-bold tracking-tight text-white">{TITLES[active]}</h1>
      </div>
      <div className="flex items-center gap-2 md:gap-3">
        <ProductSearch />
        {showFilters && active !== "shipments" && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="date-range-switcher"
                variant="outline"
                className="h-9 bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white rounded-full px-4 hidden sm:flex"
              >
                <CalendarRange className="h-4 w-4 mr-2 opacity-70" />
                <span className="font-semibold">{activePreset?.label}</span>
                <ChevronDown className="h-4 w-4 ml-1 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#111] border-[#262626] text-white">
              {presets.map((p) => (
                <DropdownMenuItem
                  key={p.id}
                  data-testid={`date-option-${p.id}`}
                  onClick={() => setPreset(p.id)}
                  className="cursor-pointer focus:bg-white/10 focus:text-white"
                >
                  {p.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              data-testid="currency-switcher"
              variant="outline"
              className="h-9 bg-[#111] border-[#262626] text-white hover:bg-[#1a1a1a] hover:text-white rounded-full px-4"
            >
              <span className="tnum font-semibold">{CURRENCY_SYMBOL[currency]} {currency}</span>
              <ChevronDown className="h-4 w-4 ml-1 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[#111] border-[#262626] text-white max-h-[60vh] overflow-y-auto">
            {(currencies || []).map((c) => (
              <DropdownMenuItem
                key={c.code}
                data-testid={`currency-option-${c.code}`}
                onClick={() => setCurrency(c.code)}
                className="cursor-pointer focus:bg-white/10 focus:text-white gap-2"
              >
                <span className="tnum font-semibold w-14">{c.symbol} {c.code}</span>
                <span className="text-xs text-[#A3A3A3]">{c.name}</span>
              </DropdownMenuItem>
            ))}
            {fxUpdatedAt && (
              <div className="px-2 py-1.5 mt-1 border-t border-[#262626] text-[10px] text-[#666]" data-testid="fx-updated-at">
                Live rates · {new Date(fxUpdatedAt).toLocaleDateString()}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          data-testid="open-upload-dialog"
          onClick={onUpload}
          className="h-9 bg-[#007AFF] hover:bg-[#005BB5] text-white rounded-full px-4 font-semibold transition-colors duration-200"
        >
          <Upload className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline">Upload Report</span>
        </Button>
      </div>
    </header>
  );
}
